import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useMarket, inPortals } from '../../contexts/MarketContext';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

// Owner (admin) financials filter — slice the money view by business unit.
const FIN_OPTIONS = [
  { key: 'all',          label: 'All' },
  { key: 'uk_to_spain',  label: 'UK to Spain' },
  { key: 'spain_to_uk',  label: 'Spain to UK' },
  { key: 'padelante',    label: 'Padelante' },
];

// Requests representing committed/active pipeline (quote approved by customer and beyond)
const PIPELINE_STATUSES = new Set(['quote_approved', 'deposit_invoice_sent', 'deposit_paid', 'pre_tour', 'tour_live']);

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n, currency = 'GBP', short = false) {
  const sym = CURRENCY_SYMBOLS[currency] || '£';
  const val = Number(n) || 0;
  if (short) {
    if (val >= 1_000_000) return `${sym}${(val / 1_000_000).toFixed(1)}m`;
    if (val >= 1_000)     return `${sym}${(val / 1_000).toFixed(1)}k`;
    return `${sym}${val.toFixed(0)}`;
  }
  return `${sym}${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function tsToDate(ts) {
  if (!ts) return null;
  return ts.toDate ? ts.toDate() : new Date(ts);
}

function fmtDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function daysBetween(a, b) {
  return Math.round((b - a) / 864e5);
}

// ── Period helpers ────────────────────────────────────────────────────────────

function getPeriodBounds(mode, offset) {
  const now = new Date();

  if (mode === 'year') {
    const y = now.getFullYear() + offset;
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59), label: String(y), chartYear: y };
  }

  if (mode === 'quarter') {
    const curQ   = Math.floor(now.getMonth() / 3);
    const totalQ = now.getFullYear() * 4 + curQ + offset;
    const y = Math.floor(totalQ / 4);
    const q = ((totalQ % 4) + 4) % 4;
    return {
      start: new Date(y, q * 3, 1),
      end:   new Date(y, q * 3 + 3, 0, 23, 59, 59),
      label: `Q${q + 1} ${y}`,
      chartYear: y,
    };
  }

  // month
  const totalM = now.getFullYear() * 12 + now.getMonth() + offset;
  const y = Math.floor(totalM / 12);
  const m = ((totalM % 12) + 12) % 12;
  return {
    start: new Date(y, m, 1),
    end:   new Date(y, m + 1, 0, 23, 59, 59),
    label: `${MONTH_FULL[m]} ${y}`,
    chartYear: y,
  };
}

// ── Format helpers ────────────────────────────────────────────────────────────

/**
 * Format a { currency: amount } totals map as a readable string.
 * short=true → abbreviated (£10.5k), short=false → full (£10,500.00)
 * Returns '—' if no positive values.
 */
function fmtMulti(totals, short = false) {
  const entries = Object.entries(totals).filter(([, v]) => v > 0);
  if (entries.length === 0) return '—';
  return entries.map(([c, v]) => fmt(v, c, short)).join(' + ');
}

function totalInvoiceCount(totals) {
  return Object.values(totals).filter((v) => v > 0).length;
}

// ── Data computation ──────────────────────────────────────────────────────────

function computeStats(scopedInvoices, scopedRequests, chartYear) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Caller pre-scopes to the active portals.
  const invoices = scopedInvoices;

  // ─ All-time KPI: total invoiced (per-currency + GBP equivalent) ─
  const allTimeInvoiced = {};
  let allTimeInvoicedGBP = 0;
  for (const inv of invoices) {
    const c = inv.currency || 'GBP';
    allTimeInvoiced[c] = (allTimeInvoiced[c] || 0) + (Number(inv.amount) || 0);
    allTimeInvoicedGBP += Number(inv.gbpEquivalent ?? inv.amount) || 0;
  }

  // ─ All-time KPI: total collected ─
  const allTimeCollected = {};
  let allTimeCollectedGBP = 0;
  for (const inv of invoices) {
    if (inv.status !== 'paid') continue;
    const c = inv.currency || 'GBP';
    allTimeCollected[c] = (allTimeCollected[c] || 0) + (Number(inv.amount) || 0);
    allTimeCollectedGBP += Number(inv.gbpEquivalent ?? inv.amount) || 0;
  }

  // ─ Outstanding (all unpaid) and overdue (past due date) ─
  const outstanding = {};
  let outstandingGBP = 0;
  const overdueList = [];
  for (const inv of invoices) {
    if (inv.status === 'paid') continue;
    const due = parseLocalDate(inv.dueDate);
    const c   = inv.currency || 'GBP';
    outstanding[c] = (outstanding[c] || 0) + (Number(inv.amount) || 0);
    outstandingGBP += Number(inv.gbpEquivalent ?? inv.amount) || 0;
    if (due && due < today) {
      overdueList.push({ ...inv, _daysOverdue: daysBetween(due, today) });
    }
  }
  overdueList.sort((a, b) => b._daysOverdue - a._daysOverdue);

  const overdueTotals = {};
  let overdueTotalsGBP = 0;
  for (const inv of overdueList) {
    const c = inv.currency || 'GBP';
    overdueTotals[c] = (overdueTotals[c] || 0) + (Number(inv.amount) || 0);
    overdueTotalsGBP += Number(inv.gbpEquivalent ?? inv.amount) || 0;
  }

  // ─ Monthly chart data (all currencies converted to GBP, by chartYear) ─
  const monthlyData = MONTH_SHORT.map((label) => ({ label, invoiced: 0, collected: 0 }));
  for (const inv of invoices) {
    const gbpAmt = Number(inv.gbpEquivalent ?? inv.amount) || 0;
    const cd = tsToDate(inv.createdAt) || today;
    if (cd.getFullYear() === chartYear) {
      monthlyData[cd.getMonth()].invoiced += gbpAmt;
    }
    if (inv.status === 'paid') {
      const pd = tsToDate(inv.paidAt);
      if (pd && pd.getFullYear() === chartYear) {
        monthlyData[pd.getMonth()].collected += gbpAmt;
      }
    }
  }

  // ─ Upcoming (next 90 days, unpaid) ─
  const upcoming = invoices
    .filter((inv) => {
      if (inv.status === 'paid') return false;
      const due = parseLocalDate(inv.dueDate);
      if (!due) return false;
      const d = daysBetween(today, due);
      return d >= 0 && d <= 90;
    })
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  // ─ Pipeline from approved+ requests ─
  const pipelineRequests = scopedRequests.filter(
    (r) => PIPELINE_STATUSES.has(r.status) && r.quoteDetails
  );
  const projRevenue    = pipelineRequests.reduce((s, r) => s + (Number(r.quoteDetails?.totalCostToCustomerGBP ?? r.quoteDetails?.totalCostToCustomer) || 0), 0);
  const projCost       = pipelineRequests.reduce((s, r) => s + (Number(r.quoteDetails?.totalCostToMSTGBP      ?? r.quoteDetails?.totalCostToMST)      || 0), 0);
  const projMargin     = projRevenue - projCost;

  return {
    allTimeInvoiced, allTimeInvoicedGBP,
    allTimeCollected, allTimeCollectedGBP,
    outstanding, outstandingGBP,
    overdueTotals, overdueTotalsGBP,
    overdueList,
    monthlyData,
    upcoming,
    projRevenue,
    projCost,
    projMargin,
    pipelineRequests,
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KPICard({ label, primary, secondary, sub, accent = 'blue' }) {
  const styles = {
    blue:   { bg: 'bg-white',            border: 'border-[#E5E7EB]',              val: '#1A4A8A' },
    green:  { bg: 'bg-white',            border: 'border-[#E5E7EB]',              val: '#1A7A4A' },
    amber:  { bg: 'bg-white',            border: 'border-[#E5E7EB]',              val: '#92400E' },
    red:    { bg: 'bg-[#FFF8F7]',        border: 'border-[#F3C6C1]',             val: '#C0392B' },
    slate:  { bg: 'bg-white',            border: 'border-[#E5E7EB]',              val: '#475569' },
    purple: { bg: 'bg-white',            border: 'border-[#E5E7EB]',              val: '#7C3AED' },
  };
  const s = styles[accent] || styles.blue;
  const isMulti = typeof primary === 'string' && primary.includes('+');
  return (
    <div className={`${s.bg} rounded-[14px] border ${s.border} shadow-[0_1px_2px_rgba(16,24,40,.04)] px-[16px] py-[15px]`}>
      <p className="text-[11.5px] font-semibold text-[#6B7280] mb-[8px]">{label}</p>
      <p className={`font-extrabold leading-tight tabular-nums ${isMulti ? 'text-[14px]' : 'text-[22px]'}`}
        style={{ color: s.val }}>{primary}</p>
      {secondary && <p className={`mt-1 ${isMulti ? 'text-[11px] text-[#9CA3AF]' : 'text-[12px] text-[#6B7280]'}`}>{secondary}</p>}
      {sub && <p className="text-[11px] text-[#9CA3AF] mt-0.5">{sub}</p>}
    </div>
  );
}

function CurrencyBreakdown({ totals, title }) {
  const entries = Object.entries(totals).filter(([, v]) => v > 0);
  if (entries.length === 0) return <span className="text-[#9CA3AF]">—</span>;
  return (
    <span className="font-bold">
      {entries.map(([c, v], i) => (
        <span key={c}>{i > 0 && <span className="text-[#9CA3AF] mx-1 font-normal">+</span>}{fmt(v, c)}</span>
      ))}
    </span>
  );
}

function BarChart({ data, height = 200 }) {
  const [tooltip, setTooltip] = useState(null);
  const maxVal = Math.max(...data.map((d) => Math.max(d.invoiced, d.collected)), 1);
  const today  = new Date();
  const currentMonthIdx = today.getMonth();

  function yLabel(val) {
    if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
    if (val >= 1_000)     return `£${(val / 1_000).toFixed(0)}k`;
    return `£${val.toFixed(0)}`;
  }

  const gridLines = 4;

  return (
    <div className="relative select-none">
      {/* Y-axis labels */}
      <div className="flex">
        <div className="w-12 flex-shrink-0 flex flex-col justify-between items-end pr-2 pb-6" style={{ height }}>
          {Array.from({ length: gridLines + 1 }, (_, i) => (
            <span key={i} className="text-[10px] text-[#9CA3AF]">
              {yLabel((maxVal * (gridLines - i)) / gridLines)}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div className="flex-1 relative" style={{ height }}>
          {/* Grid lines */}
          {Array.from({ length: gridLines }, (_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 border-t border-dashed border-[#F3F4F6]"
              style={{ top: `${((i + 1) / gridLines) * (height - 24)}px` }}
            />
          ))}

          {/* Bars */}
          <div className="absolute inset-0 flex items-end gap-px pb-6">
            {data.map((d, i) => {
              const invH  = maxVal > 0 ? ((d.invoiced  / maxVal) * (height - 24)) : 0;
              const colH  = maxVal > 0 ? ((d.collected / maxVal) * (height - 24)) : 0;
              const isPast = i < currentMonthIdx;
              const isCur  = i === currentMonthIdx;

              return (
                <div
                  key={i}
                  className="relative flex-1 flex flex-col justify-end gap-px cursor-pointer group"
                  style={{ height: height - 24 }}
                  onMouseEnter={() => setTooltip({ i, d })}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {/* Invoiced bar */}
                  <div
                    className={`w-full rounded-t transition-all duration-300 ${
                      isCur ? 'bg-pb' : isPast ? 'bg-pb/70' : 'bg-pb/30'
                    } group-hover:opacity-80`}
                    style={{ height: Math.max(invH, d.invoiced > 0 ? 2 : 0) }}
                  />
                  {/* Collected bar — overlaid in front of the invoiced bar,
                      positioned within this month's column. */}
                  {d.collected > 0 && (
                    <div
                      className="rounded-t transition-all duration-300 bg-emerald-500/80 group-hover:opacity-90"
                      style={{
                        height: Math.max(colH, 2),
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Month labels */}
          <div className="absolute bottom-0 left-0 right-0 flex gap-px h-6">
            {data.map((d, i) => (
              <div key={i} className={`flex-1 flex items-center justify-center text-[10px] font-medium ${
                i === currentMonthIdx ? 'text-pb' : 'text-[#9CA3AF]'
              }`}>
                {d.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="absolute z-10 bg-navy text-white text-[11px] rounded-[8px] px-3 py-2 shadow-lg pointer-events-none whitespace-nowrap"
          style={{ top: 0, left: `${(tooltip.i / data.length) * 100}%`, transform: 'translateX(-50%)' }}
        >
          <p className="font-semibold mb-1">{MONTH_FULL[tooltip.i]}</p>
          <p><span className="text-white/60">Invoiced:&nbsp;</span>{fmt(tooltip.d.invoiced)}</p>
          <p><span className="text-white/60">Collected:&nbsp;</span>{fmt(tooltip.d.collected)}</p>
        </div>
      )}
    </div>
  );
}

function UpcomingBand({ label, invoices, accent }) {
  if (invoices.length === 0) return null;
  const total = invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const currency = invoices[0]?.currency || 'GBP';
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[11px] font-bold uppercase tracking-wider ${accent}`}>{label}</span>
        <span className="text-[12px] font-semibold text-[#374151]">{fmt(total, currency)}</span>
      </div>
      <div className="space-y-1">
        {invoices.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between py-[9px] px-3 rounded-[9px] bg-surface text-[12px]">
            <span className="text-[#374151] font-semibold truncate mr-3">{inv.requestSummary || inv.invoiceNumber}</span>
            <span className="text-[#9CA3AF] whitespace-nowrap">Due {fmtDate(inv.dueDate)}</span>
            <span className="font-bold text-[#111827] ml-3 whitespace-nowrap tabular-nums">
              {fmt(inv.amount, inv.currency || 'GBP')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FinancialDashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { portals } = useMarket();

  const [invoices,          setInvoices]          = useState([]);
  const [requests,          setRequests]          = useState([]);
  const [expenses,          setExpenses]          = useState([]);
  const [supplierBookings,  setSupplierBookings]  = useState([]);
  const [loading,           setLoading]           = useState(true);

  const [periodMode,   setPeriodMode]   = useState('year');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [financialsPortal, setFinancialsPortal] = useState('all');

  // Owner business filter (admin, and only when the shell spans >1 portal).
  const showFinancialsFilter = isAdmin && portals.length > 1;
  const effectivePortals = (showFinancialsFilter && financialsPortal !== 'all') ? [financialsPortal] : portals;
  const effKey = effectivePortals.join(',');

  // Scope every dataset to the active portals. Invoices/requests carry
  // market+brand; expenses/supplier bookings are scoped via their parent tour.
  const scopedInvoices = useMemo(() => invoices.filter((i) => inPortals(i, effectivePortals)), [invoices, effKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopedRequests = useMemo(() => requests.filter((r) => inPortals(r, effectivePortals)), [requests, effKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopedRequestIds = useMemo(() => new Set(scopedRequests.map((r) => r.id)), [scopedRequests]);
  const scopedExpenses = useMemo(
    () => expenses.filter((e) => !e.storedOnly && (e.requestId ? scopedRequestIds.has(e.requestId) : inPortals(e, effectivePortals))),
    [expenses, scopedRequestIds, effKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopedBookings = useMemo(
    () => supplierBookings.filter((a) => (a.requestId ? scopedRequestIds.has(a.requestId) : true)),
    [supplierBookings, scopedRequestIds]);

  useEffect(() => {
    let invoicesDone = false, requestsDone = false;
    const check = () => { if (invoicesDone && requestsDone) setLoading(false); };

    const u1 = onSnapshot(collection(db, 'invoices'), (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      invoicesDone = true; check();
    });
    const u2 = onSnapshot(collection(db, 'requests'), (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !r.crmOrigin || r.crmFullyConverted));
      requestsDone = true; check();
    });
    const u3 = onSnapshot(collection(db, 'expenses'), (snap) => {
      setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const u4 = onSnapshot(collection(db, 'bookingSuppliers'), (snap) => {
      setSupplierBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  const period = getPeriodBounds(periodMode, periodOffset);
  const stats  = loading ? null : computeStats(scopedInvoices, scopedRequests, period.chartYear);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Expenses in period (chart period still applies to expenses section)
  const periodExpenses = scopedExpenses.filter((e) => {
    const d = parseLocalDate(e.date);
    return d && d >= period.start && d <= period.end;
  });
  // Period totals grouped by currency
  const periodTotalsByCurrency = periodExpenses.reduce((acc, e) => {
    const c = e.currency || 'GBP';
    acc[c] = (acc[c] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  // ── Cost Overview (all-time, GBP equivalents) ──
  const mstExpenses = scopedExpenses;
  let costPaidGBP  = 0;
  let costToPayGBP = 0;
  for (const e of mstExpenses) {
    const gbp = Number(e.gbpEquivalent ?? e.amount) || 0;
    if ((e.paymentStatus || 'paid') === 'paid') costPaidGBP  += gbp;
    else if (e.paymentStatus === 'to_pay')       costToPayGBP += gbp;
  }

  // Active tours: pipeline statuses — all amounts in GBP equivalents
  const activeTourRows = scopedRequests
    .filter((r) => PIPELINE_STATUSES.has(r.status) && r.quoteDetails?.totalCostToMST)
    .map((r) => {
      const quoteCost   = Number(r.quoteDetails.totalCostToMSTGBP ?? r.quoteDetails.totalCostToMST) || 0;
      const tourExp     = mstExpenses.filter((e) => e.requestId === r.id);
      const tourPaid    = tourExp.filter((e) => (e.paymentStatus || 'paid') === 'paid').reduce((s, e) => s + (Number(e.gbpEquivalent ?? e.amount) || 0), 0);
      const tourToPay   = tourExp.filter((e) => e.paymentStatus === 'to_pay').reduce((s, e) => s + (Number(e.gbpEquivalent ?? e.amount) || 0), 0);
      const notYetInv   = Math.max(0, quoteCost - tourPaid - tourToPay);
      return { r, quoteCost, tourPaid, tourToPay, notYetInv };
    })
    .sort((a, b) => (a.r.departureDate || '').localeCompare(b.r.departureDate || ''));

  const notYetInvGBP = activeTourRows.reduce((s, row) => s + row.notYetInv, 0);

  // Single GBP footer row for the breakdown table
  const footerTotals = [{
    cur: 'GBP',
    paid:      activeTourRows.reduce((s, row) => s + row.tourPaid,   0),
    toPay:     activeTourRows.reduce((s, row) => s + row.tourToPay,  0),
    notYetInv: activeTourRows.reduce((s, row) => s + row.notYetInv,  0),
  }];

  // Split upcoming into 30-day bands
  const band1 = stats?.upcoming.filter((i) => daysBetween(today, parseLocalDate(i.dueDate)) <= 30) ?? [];
  const band2 = stats?.upcoming.filter((i) => { const d = daysBetween(today, parseLocalDate(i.dueDate)); return d > 30 && d <= 60; }) ?? [];
  const band3 = stats?.upcoming.filter((i) => { const d = daysBetween(today, parseLocalDate(i.dueDate)); return d > 60 && d <= 90; }) ?? [];

  if (loading) {
    return <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-12 text-center text-[#9CA3AF] text-[13px]">Loading financial data…</div>;
  }

  const { allTimeInvoiced, allTimeInvoicedGBP,
          allTimeCollected, allTimeCollectedGBP,
          outstanding, outstandingGBP,
          overdueTotals, overdueTotalsGBP,
          overdueList, monthlyData, projRevenue, projCost, projMargin, pipelineRequests } = stats;

  const totalUnpaidCount = scopedInvoices.filter((i) => i.status !== 'paid').length;

  // Supplier invoice stats
  const totalAssignments    = scopedBookings.length;
  const invoicesReceived    = scopedBookings.filter((a) => !!a.invoiceUrl).length;
  const supplierInvoicePct  = totalAssignments > 0 ? Math.round((invoicesReceived / totalAssignments) * 100) : 0;

  // Supplier invoices missing for active (non-declined) confirmed tours
  const activeConfirmedMissing = scopedBookings.filter(
    (a) => a.status === 'confirmed' && !a.invoiceUrl
  ).length;

  // Supplier invoice cost totals (from invoiceAmount field on each booking)
  const supplierCostByCurrency = {};
  let supplierCostGBP = 0;
  for (const a of scopedBookings) {
    if (a.invoiceAmount == null) continue;
    const c = a.invoiceCurrency || 'GBP';
    const amt = Number(a.invoiceAmount) || 0;
    supplierCostByCurrency[c] = (supplierCostByCurrency[c] || 0) + amt;
    const rate = c === 'EUR' ? 0.86 : c === 'USD' ? 0.79 : 1;
    supplierCostGBP += amt * rate;
  }

  // Multi-currency pipeline totals
  const pipelineRevByCur    = {};
  const pipelineCostByCur   = {};
  for (const r of pipelineRequests) {
    const cur = r.currency || 'GBP';
    pipelineRevByCur[cur]  = (pipelineRevByCur[cur]  || 0) + (Number(r.quoteDetails?.totalCostToCustomer) || 0);
    pipelineCostByCur[cur] = (pipelineCostByCur[cur] || 0) + (Number(r.quoteDetails?.totalCostToMST)      || 0);
  }
  const pipelineMarginByCur = Object.fromEntries(
    [...new Set([...Object.keys(pipelineRevByCur), ...Object.keys(pipelineCostByCur)])].map((cur) => [
      cur, (pipelineRevByCur[cur] || 0) - (pipelineCostByCur[cur] || 0),
    ])
  );

  return (
    <div className="space-y-5">

      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Financials</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            All-time invoice totals, pipeline, and cost overview.
            {financialsPortal === 'all' && ' All currencies converted to £.'}
          </p>
        </div>
        {showFinancialsFilter && (
          <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[9px] p-[3px] gap-[2px]">
            {FIN_OPTIONS.map((opt) => (
              <button key={opt.key} onClick={() => setFinancialsPortal(opt.key)}
                className={`px-3 py-[5px] rounded-[7px] text-[12px] font-semibold transition ${financialsPortal === opt.key ? 'bg-navy text-white' : 'text-[#6B7280] hover:text-[#374151]'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Period selector ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] px-5 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-surface rounded-[9px] p-[3px] gap-[2px]">
            {[['year', 'Year'], ['quarter', 'Quarter'], ['month', 'Month']].map(([mode, label]) => (
              <button key={mode} onClick={() => { setPeriodMode(mode); setPeriodOffset(0); }}
                className={`px-4 py-[6px] rounded-[7px] text-[12.5px] font-semibold transition ${
                  periodMode === mode ? 'bg-white text-navy shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'
                }`}>
                {label}
              </button>
            ))}
          </div>
          <span className="text-[11.5px] text-[#9CA3AF]">Controls chart &amp; expenses</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[14px] font-extrabold text-navy min-w-[140px] text-center">{period.label}</span>
          <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[9px] overflow-hidden">
            <button onClick={() => setPeriodOffset((o) => o - 1)} className="px-3 py-[7px] text-[#6B7280] hover:bg-surface transition">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="w-px h-5 bg-[#E5E7EB]" />
            <button onClick={() => setPeriodOffset((o) => o + 1)} className="px-3 py-[7px] text-[#6B7280] hover:bg-surface transition">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          {periodOffset !== 0 && (
            <button onClick={() => setPeriodOffset(0)}
              className="text-[12.5px] font-bold text-pb hover:text-navy border border-[#E5E7EB] hover:border-pb/40 px-3 py-[7px] rounded-[9px] transition">
              Current
            </button>
          )}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KPICard
          label="Invoiced"
          primary={fmt(allTimeInvoicedGBP, 'GBP', true)}
          secondary={fmtMulti(allTimeInvoiced, false)}
          sub={`${scopedInvoices.length} invoice${scopedInvoices.length !== 1 ? 's' : ''} raised`}
          accent="blue"
        />
        <KPICard
          label="Collected"
          primary={fmt(allTimeCollectedGBP, 'GBP', true)}
          secondary={fmtMulti(allTimeCollected, false)}
          sub={scopedInvoices.length > 0
            ? `${scopedInvoices.filter((i) => i.status === 'paid').length} of ${scopedInvoices.length} paid`
            : undefined}
          accent="green"
        />
        <KPICard
          label="Outstanding"
          primary={fmt(outstandingGBP, 'GBP', true)}
          secondary={fmtMulti(outstanding, false)}
          sub={`${totalUnpaidCount} unpaid invoice${totalUnpaidCount !== 1 ? 's' : ''}`}
          accent="amber"
        />
        <KPICard
          label="Overdue"
          primary={fmt(overdueTotalsGBP, 'GBP', true)}
          secondary={fmtMulti(overdueTotals, false)}
          sub={overdueList.length > 0 ? `${overdueList.length} invoice${overdueList.length !== 1 ? 's' : ''} past due` : 'All clear'}
          accent={overdueList.length > 0 ? 'red' : 'green'}
        />
        <KPICard
          label="Pipeline Revenue"
          primary={fmt(projRevenue, 'GBP', true)}
          secondary={fmtMulti(pipelineRevByCur, false)}
          sub={`${pipelineRequests.length} approved tour${pipelineRequests.length !== 1 ? 's' : ''}`}
          accent="purple"
        />
        <KPICard
          label="Pipeline Margin"
          primary={fmt(projMargin, 'GBP', true)}
          secondary={fmtMulti(pipelineMarginByCur, false)}
          sub={projRevenue > 0 ? `${Math.round((projMargin / projRevenue) * 100)}% margin` : undefined}
          accent={projMargin >= 0 ? 'green' : 'red'}
        />
        <KPICard
          label="Supplier Invoices"
          primary={`${invoicesReceived}/${totalAssignments}`}
          secondary={`${supplierInvoicePct}% received`}
          sub={activeConfirmedMissing > 0 ? `${activeConfirmedMissing} confirmed, no invoice` : 'All confirmed have invoices'}
          accent={activeConfirmedMissing > 0 ? 'amber' : 'green'}
        />
        <KPICard
          label="Supplier Invoice Cost"
          primary={supplierCostGBP > 0 ? fmt(supplierCostGBP, 'GBP', true) : '—'}
          secondary={Object.keys(supplierCostByCurrency).length > 0 ? fmtMulti(supplierCostByCurrency, false) : undefined}
          sub={`${invoicesReceived} invoice${invoicesReceived !== 1 ? 's' : ''} with amounts`}
          accent="blue"
        />
      </div>

      {/* ── Revenue chart + Cash flow ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* Revenue chart */}
        <div className="xl:col-span-2 bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-[14.5px] font-bold text-[#111827]">Revenue — {period.chartYear}</h3>
              <p className="text-[12px] text-[#6B7280] mt-0.5">All currencies · converted to GBP · monthly view</p>
            </div>
            <div className="flex items-center gap-4 text-[12px]">
              <div className="flex items-center gap-1.5">
                <span className="w-[9px] h-[9px] rounded-[3px] bg-pb" />
                <span className="text-[#6B7280] font-semibold">Invoiced</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-[9px] h-[9px] rounded-[3px] bg-sg" />
                <span className="text-[#6B7280] font-semibold">Collected</span>
              </div>
            </div>
          </div>
          <BarChart data={monthlyData} height={220} />
        </div>

        {/* Upcoming Payments */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <h3 className="text-[14.5px] font-bold text-[#111827]">Upcoming Payments</h3>
          <p className="text-[12px] text-[#6B7280] mt-0.5 mb-5">Next 90 days · unpaid invoices</p>
          {band1.length === 0 && band2.length === 0 && band3.length === 0 ? (
            <p className="text-[13px] text-[#9CA3AF]">No payments due in the next 90 days.</p>
          ) : (
            <div className="space-y-5">
              <UpcomingBand label="Next 30 days"  invoices={band1} accent="text-dr" />
              <UpcomingBand label="31 – 60 days"  invoices={band2} accent="text-[#92400E]" />
              <UpcomingBand label="61 – 90 days"  invoices={band3} accent="text-[#6B7280]" />
            </div>
          )}
        </div>
      </div>

      {/* ── Overdue + Pipeline ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        {/* Overdue invoices */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
            <div>
              <h3 className="text-[14.5px] font-bold text-[#111827]">Overdue Invoices</h3>
              <p className="text-[12px] text-[#6B7280] mt-0.5">Unpaid and past due date</p>
            </div>
            {overdueList.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-red-light text-dr">
                <span className="w-[5px] h-[5px] rounded-full bg-dr flex-shrink-0" />
                {overdueList.length} overdue
              </span>
            )}
          </div>
          {overdueList.length === 0 ? (
            <div className="px-[18px] py-10 text-center">
              <div className="w-10 h-10 rounded-full bg-green-light flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-sg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-[13px] font-bold text-[#374151]">No overdue invoices</p>
              <p className="text-[12px] text-[#9CA3AF] mt-0.5">All payments are on track.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  {['Tour','Due','Days','Amount'].map((h, i) => (
                    <th key={h} className={`px-[18px] py-[9px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] ${i === 3 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F6]">
                {overdueList.map((inv) => (
                  <tr key={inv.id} onClick={() => navigate(`/staff/requests/${inv.requestId}`)}
                    className="hover:bg-surface cursor-pointer transition">
                    <td className="px-[18px] py-[11px]">
                      <p className="text-[13px] font-bold text-[#111827]">{inv.requestSummary || inv.invoiceNumber}</p>
                      <p className="text-[11.5px] text-[#9CA3AF]">{inv.customerEmail}</p>
                    </td>
                    <td className="px-[18px] py-[11px] text-[12.5px] text-[#374151] whitespace-nowrap">{fmtDate(inv.dueDate)}</td>
                    <td className="px-[18px] py-[11px]">
                      <span className="inline-flex items-center px-[7px] py-[2px] rounded-[20px] text-[11px] font-bold bg-red-light text-dr">
                        {inv._daysOverdue}d
                      </span>
                    </td>
                    <td className="px-[18px] py-[11px] text-right font-bold text-[13px] text-[#111827] whitespace-nowrap tabular-nums">
                      {fmt(inv.amount, inv.currency || 'GBP')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Revenue Pipeline */}
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
            <div>
              <h3 className="text-[14.5px] font-bold text-[#111827]">Revenue Pipeline</h3>
              <p className="text-[12px] text-[#6B7280] mt-0.5">Committed tours with live quotes</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-[#9CA3AF]">Projected revenue</p>
              <p className="text-[14px] font-extrabold tabular-nums" style={{ color: '#7C3AED' }}>{fmtMulti(pipelineRevByCur)}</p>
            </div>
          </div>
          {pipelineRequests.length === 0 ? (
            <div className="px-[18px] py-10 text-center">
              <p className="text-[13px] text-[#9CA3AF]">No pipeline tours found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead className="bg-surface border-b border-[#E5E7EB]">
                  <tr>
                    {['Tour','Departure','Revenue','Cost','Margin'].map((h, i) => (
                      <th key={h} className={`px-[18px] py-[9px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] ${i >= 2 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F3F6]">
                  {pipelineRequests
                    .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
                    .map((req) => {
                      const rev    = Number(req.quoteDetails?.totalCostToCustomer) || 0;
                      const cost   = Number(req.quoteDetails?.totalCostToMST)      || 0;
                      const margin = rev - cost;
                      const pct    = rev > 0 ? Math.round((margin / rev) * 100) : 0;
                      const cur    = req.currency || 'GBP';
                      return (
                        <tr key={req.id} onClick={() => navigate(`/staff/requests/${req.id}`)}
                          className="hover:bg-surface cursor-pointer transition">
                          <td className="px-[18px] py-[11px]">
                            <p className="text-[13px] font-bold text-[#111827]">{req.sport || '—'}{req.destination ? ` — ${req.destination}` : ''}</p>
                            <p className="text-[11.5px] text-[#9CA3AF] capitalize">{req.status?.replace(/_/g, ' ')}</p>
                          </td>
                          <td className="px-[18px] py-[11px] text-[12.5px] text-[#374151] whitespace-nowrap">{fmtDate(req.departureDate)}</td>
                          <td className="px-[18px] py-[11px] text-right text-[12.5px] font-bold text-[#111827] whitespace-nowrap tabular-nums">{fmt(rev, cur)}</td>
                          <td className="px-[18px] py-[11px] text-right text-[12.5px] text-[#6B7280] whitespace-nowrap tabular-nums">{fmt(cost, cur)}</td>
                          <td className="px-[18px] py-[11px] text-right whitespace-nowrap">
                            <span className={`text-[12.5px] font-bold tabular-nums ${margin >= 0 ? 'text-sg' : 'text-dr'}`}>{fmt(margin, cur)}</span>
                            <span className={`ml-1.5 text-[10.5px] font-bold px-[6px] py-[2px] rounded-[20px] ${
                              pct >= 20 ? 'bg-green-light text-sg' : pct >= 10 ? 'bg-amber-light text-[#92400E]' : 'bg-red-light text-dr'
                            }`}>{pct}%</span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot className="bg-surface border-t border-[#E5E7EB]">
                  {[...new Set(pipelineRequests.map((r) => r.currency || 'GBP'))].map((cur) => {
                    const tRev    = pipelineRevByCur[cur]    || 0;
                    const tCost   = pipelineCostByCur[cur]   || 0;
                    const tMargin = pipelineMarginByCur[cur] || 0;
                    return (
                      <tr key={cur}>
                        <td colSpan={2} className="px-[18px] py-[10px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">Totals ({cur})</td>
                        <td className="px-[18px] py-[10px] text-right text-[13px] font-bold text-[#111827] tabular-nums">{fmt(tRev, cur)}</td>
                        <td className="px-[18px] py-[10px] text-right text-[13px] font-bold text-[#6B7280] tabular-nums">{fmt(tCost, cur)}</td>
                        <td className="px-[18px] py-[10px] text-right">
                          <span className={`text-[13px] font-bold tabular-nums ${tMargin >= 0 ? 'text-sg' : 'text-dr'}`}>{fmt(tMargin, cur)}</span>
                          {tRev > 0 && <span className="ml-1.5 text-[11px] text-[#9CA3AF]">{Math.round((tMargin / tRev) * 100)}%</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Expenses summary ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">Expenses — {period.label}</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">Logged costs for MST Sports Tours</p>
          </div>
          <button onClick={() => navigate('/staff/expenses')}
            className="text-[12.5px] font-bold text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-[8px] rounded-[9px] transition">
            Manage →
          </button>
        </div>
        {periodExpenses.length === 0 ? (
          <div className="border-2 border-dashed border-[#E5E7EB] rounded-[11px] py-8 text-center">
            <p className="text-[13px] text-[#9CA3AF] mb-1">No expenses logged for this period.</p>
            <button onClick={() => navigate('/staff/expenses')} className="text-[13px] font-bold text-pb hover:text-navy transition">
              + Log an expense
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(
                periodExpenses.reduce((acc, e) => {
                  const key = `${e.category || 'Other'}__${e.currency || 'GBP'}`;
                  acc[key] = (acc[key] || 0) + (Number(e.amount) || 0);
                  return acc;
                }, {})
              )
                .sort((a, b) => b[1] - a[1])
                .slice(0, 4)
                .map(([key, total]) => {
                  const [cat, cur] = key.split('__');
                  return (
                    <div key={key} className="bg-surface rounded-[11px] px-4 py-3">
                      <p className="text-[11.5px] font-semibold text-[#6B7280] mb-1">{cat}</p>
                      <p className="text-[15px] font-extrabold text-[#111827] tabular-nums">{fmt(total, cur)}</p>
                    </div>
                  );
                })}
            </div>
            <div className="text-right">
              <p className="text-[11.5px] text-[#9CA3AF] mb-0.5">Total expenses</p>
              <p className="text-[20px] font-extrabold text-[#111827] tabular-nums">{fmtMulti(periodTotalsByCurrency)}</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Cost Overview ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">Cost Overview</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">All-time · all currencies · active tour commitments</p>
          </div>
        </div>

        {/* KPI row */}
        {(() => {
          const paidCount  = mstExpenses.filter((e) => (e.paymentStatus || 'paid') === 'paid').length;
          const toPayCount = mstExpenses.filter((e) => e.paymentStatus === 'to_pay').length;
          return (
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#F1F3F6]">
              <div className="px-[18px] py-5">
                <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[8px]">Total Paid</p>
                <p className="font-extrabold text-sg leading-tight text-[22px] tabular-nums">{costPaidGBP > 0 ? fmt(costPaidGBP, 'GBP') : <span className="text-[#9CA3AF]">—</span>}</p>
                <p className="text-[11.5px] text-[#9CA3AF] mt-1">{paidCount} expense{paidCount !== 1 ? 's' : ''} marked paid</p>
              </div>
              <div className="px-[18px] py-5">
                <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[8px]">Invoices To Pay</p>
                <p className="font-extrabold leading-tight text-[22px] tabular-nums" style={{ color: '#E8A020' }}>{costToPayGBP > 0 ? fmt(costToPayGBP, 'GBP') : <span className="text-[#9CA3AF]">—</span>}</p>
                <p className="text-[11.5px] text-[#9CA3AF] mt-1">{toPayCount} invoice{toPayCount !== 1 ? 's' : ''} awaiting payment</p>
              </div>
              <div className="px-[18px] py-5">
                <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[8px]">Not Yet Invoiced</p>
                <p className="font-extrabold text-[#6B7280] leading-tight text-[22px] tabular-nums">{notYetInvGBP > 0 ? fmt(notYetInvGBP, 'GBP') : <span className="text-[#9CA3AF]">—</span>}</p>
                <p className="text-[11.5px] text-[#9CA3AF] mt-1">Across {activeTourRows.length} active tour{activeTourRows.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          );
        })()}

        {/* Per-tour breakdown */}
        {activeTourRows.length > 0 && (
          <div className="border-t border-[#F1F3F6] overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead className="bg-surface border-b border-[#E5E7EB]">
                <tr>
                  {['Tour','Departure','Quote Cost','Paid','To Pay','Not Yet Invoiced'].map((h, i) => (
                    <th key={h} className={`px-[18px] py-[9px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] ${i >= 2 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F6]">
                {activeTourRows.map(({ r, quoteCost, tourPaid, tourToPay, notYetInv }) => (
                  <tr key={r.id} onClick={() => navigate(`/staff/requests/${r.id}`)} className="hover:bg-surface cursor-pointer transition">
                    <td className="px-[18px] py-[11px]">
                      <p className="text-[13px] font-bold text-[#111827]">{r.sport || '—'}{r.destination ? ` — ${r.destination}` : ''}</p>
                      <p className="text-[11.5px] text-[#9CA3AF] capitalize">{r.status?.replace(/_/g, ' ')}</p>
                    </td>
                    <td className="px-[18px] py-[11px] text-[12.5px] text-[#374151] whitespace-nowrap">{fmtDate(r.departureDate)}</td>
                    <td className="px-[18px] py-[11px] text-right text-[12.5px] font-bold text-[#111827] tabular-nums whitespace-nowrap">{fmt(quoteCost, '£')}</td>
                    <td className="px-[18px] py-[11px] text-right text-[12.5px] font-bold text-sg tabular-nums whitespace-nowrap">{tourPaid > 0 ? fmt(tourPaid, '£') : '—'}</td>
                    <td className="px-[18px] py-[11px] text-right text-[12.5px] font-bold tabular-nums whitespace-nowrap" style={{ color: '#E8A020' }}>{tourToPay > 0 ? fmt(tourToPay, '£') : '—'}</td>
                    <td className="px-[18px] py-[11px] text-right text-[12.5px] text-[#6B7280] font-bold tabular-nums whitespace-nowrap">
                      {notYetInv > 0 ? fmt(notYetInv, '£') : <span className="text-sg text-[11px] font-bold">All accounted for</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-surface border-t border-[#E5E7EB]">
                {footerTotals.map(({ cur, paid, toPay, notYetInv: nyi }) => (
                  <tr key={cur}>
                    <td colSpan={3} className="px-[18px] py-[10px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">Totals (GBP equiv.)</td>
                    <td className="px-[18px] py-[10px] text-right text-[13px] font-extrabold text-sg tabular-nums">{paid > 0 ? fmt(paid, '£') : '—'}</td>
                    <td className="px-[18px] py-[10px] text-right text-[13px] font-extrabold tabular-nums" style={{ color: '#E8A020' }}>{toPay > 0 ? fmt(toPay, '£') : '—'}</td>
                    <td className="px-[18px] py-[10px] text-right text-[13px] font-extrabold text-[#6B7280] tabular-nums">{nyi > 0 ? fmt(nyi, '£') : '—'}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Supplier Invoices summary ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">Supplier Invoices</h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">Incoming invoices uploaded by suppliers</p>
          </div>
          <div className="flex items-center gap-3">
            {activeConfirmedMissing > 0 && (
              <span className="inline-flex items-center gap-1.5 px-[9px] py-[4px] rounded-[20px] text-[11.5px] font-bold bg-amber-light text-[#92400E]">
                <span className="w-[5px] h-[5px] rounded-full bg-ba flex-shrink-0" />
                {activeConfirmedMissing} missing
              </span>
            )}
            <button
              onClick={() => navigate('/staff/supplier-invoices')}
              className="text-[12.5px] font-bold text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-4 py-[8px] rounded-[9px] transition whitespace-nowrap"
            >
              View all →
            </button>
          </div>
        </div>

        {totalAssignments === 0 ? (
          <div className="px-[18px] py-8 text-center">
            <p className="text-[13px] text-[#9CA3AF]">No supplier assignments yet.</p>
          </div>
        ) : (
          <>
            <div className="px-[18px] py-4 border-b border-[#F1F3F6]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-semibold text-[#374151]">{invoicesReceived} of {totalAssignments} invoices received</p>
                <p className="text-[12px] font-bold text-[#374151]">{supplierInvoicePct}%</p>
              </div>
              <div className="w-full bg-[#E5E7EB] rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${supplierInvoicePct}%`,
                    background: supplierInvoicePct === 100 ? '#1A7A4A' : supplierInvoicePct >= 50 ? '#E8A020' : '#C0392B',
                  }}
                />
              </div>
            </div>

            {activeConfirmedMissing > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]">
                  <thead className="bg-surface border-b border-[#E5E7EB]">
                    <tr>
                      {['Tour', 'Departure', 'Supplier', 'Service'].map((h) => (
                        <th key={h} className="px-[18px] py-[9px] text-left text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F3F6]">
                    {scopedBookings
                      .filter((a) => a.status === 'confirmed' && !a.invoiceUrl)
                      .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
                      .slice(0, 8)
                      .map((a) => (
                        <tr key={a.id} className="hover:bg-surface transition-colors">
                          <td className="px-[18px] py-[10px]">
                            <p className="text-[13px] font-bold text-[#111827]">{a.sport}{a.destination ? ` — ${a.destination}` : ''}</p>
                          </td>
                          <td className="px-[18px] py-[10px] text-[12.5px] text-[#374151] whitespace-nowrap">{fmtDate(a.departureDate)}</td>
                          <td className="px-[18px] py-[10px]">
                            <p className="text-[13px] font-semibold text-[#111827]">{a.supplierName || '—'}</p>
                            <p className="text-[11.5px] text-[#9CA3AF]">{a.supplierEmail}</p>
                          </td>
                          <td className="px-[18px] py-[10px]">
                            <span className="text-[11.5px] font-semibold bg-[#F3F4F6] text-[#374151] px-[8px] py-[3px] rounded-[20px]">{a.serviceType || '—'}</span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {activeConfirmedMissing > 8 && (
                  <div className="px-[18px] py-3 bg-surface border-t border-[#F1F3F6]">
                    <button onClick={() => navigate('/staff/supplier-invoices')}
                      className="text-[12.5px] font-bold text-pb hover:text-navy transition">
                      View all {activeConfirmedMissing} missing invoices →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-[18px] py-6 text-center">
                <div className="w-9 h-9 rounded-full bg-green-light flex items-center justify-center mx-auto mb-2">
                  <svg className="w-4 h-4 text-sg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-[13px] font-bold text-[#374151]">All confirmed suppliers have submitted invoices</p>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}
